import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';

interface StatusOption {
  id: number;
  status: string;
}

interface StatusSelectionComponentProps {
  searchBy: string;
  ccmStatusOptions: StatusOption[];
  rpmStatusOptions: StatusOption[];
  selectedCCMStatuses: string[];
  selectedRPMStatuses: string[];
  onCCMStatusChange: (status: string, checked: boolean) => void;
  onRPMStatusChange: (status: string, checked: boolean) => void;
  onResetCCMStatuses: () => void;
  onResetRPMStatuses: () => void;
  onResetAllStatuses: () => void;
}

const StatusSelectionComponent: React.FC<StatusSelectionComponentProps> = ({
  searchBy,
  ccmStatusOptions,
  rpmStatusOptions,
  selectedCCMStatuses,
  selectedRPMStatuses,
  onCCMStatusChange,
  onRPMStatusChange,
  onResetCCMStatuses,
  onResetRPMStatuses,
  onResetAllStatuses
}) => {
  // CCM Status Selection
  if (searchBy === 'ccm_status') {
    return (
      <div className="mt-4 p-6 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 rounded-xl border border-purple-200 shadow-lg" data-testid="status-selection-ccm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-purple-700">Select CCM Status</h3>
          <button
            onClick={onResetCCMStatuses}
            className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full flex items-center justify-center transition-all cursor-pointer"
            title="Reset CCM Status Selection"
            data-testid="status-selection-ccm-reset-button"
          >
            <span className="text-white text-sm font-bold">↻</span>
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {ccmStatusOptions.map((option, index) => {
            const colors = [
              'from-blue-100 to-blue-200 border-blue-300 text-blue-800',
              'from-green-100 to-green-200 border-green-300 text-green-800',
              'from-purple-100 to-purple-200 border-purple-300 text-purple-800',
              'from-pink-100 to-pink-200 border-pink-300 text-pink-800',
              'from-indigo-100 to-indigo-200 border-indigo-300 text-indigo-800',
              'from-teal-100 to-teal-200 border-teal-300 text-teal-800',
              'from-orange-100 to-orange-200 border-orange-300 text-orange-800',
              'from-cyan-100 to-cyan-200 border-cyan-300 text-cyan-800',
              'from-emerald-100 to-emerald-200 border-emerald-300 text-emerald-800',
              'from-rose-100 to-rose-200 border-rose-300 text-rose-800'
            ];
            const colorClass = colors[index % colors.length];

            return (
              <div key={option.id} className={`bg-gradient-to-r ${colorClass} p-3 rounded-lg border-2 hover:shadow-md transition-all duration-200 cursor-pointer ${selectedCCMStatuses.includes(option.status) ? 'ring-2 ring-purple-400 shadow-lg' : ''}`}>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`ccm-${option.id}`}
                    checked={selectedCCMStatuses.includes(option.status)}
                    onCheckedChange={(checked) =>
                      onCCMStatusChange(option.status, checked as boolean)
                    }
                    className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                    data-testid={`status-selection-ccm-checkbox-${option.status.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                  <label htmlFor={`ccm-${option.id}`} className="text-sm font-medium cursor-pointer flex-1">
                    {option.status}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // RPM Status Selection
  if (searchBy === 'rpm_status') {
    return (
      <div className="mt-4 p-6 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 rounded-xl border border-teal-200 shadow-lg" data-testid="status-selection-rpm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-teal-700">Select RPM Status</h3>
          <button
            onClick={onResetRPMStatuses}
            className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full flex items-center justify-center transition-all cursor-pointer"
            title="Reset RPM Status Selection"
            data-testid="status-selection-rpm-reset-button"
          >
            <span className="text-white text-sm font-bold">↻</span>
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {rpmStatusOptions.map((option, index) => {
            const colors = [
              'from-teal-100 to-teal-200 border-teal-300 text-teal-800',
              'from-cyan-100 to-cyan-200 border-cyan-300 text-cyan-800',
              'from-blue-100 to-blue-200 border-blue-300 text-blue-800',
              'from-indigo-100 to-indigo-200 border-indigo-300 text-indigo-800',
              'from-purple-100 to-purple-200 border-purple-300 text-purple-800',
              'from-pink-100 to-pink-200 border-pink-300 text-pink-800',
              'from-rose-100 to-rose-200 border-rose-300 text-rose-800',
              'from-orange-100 to-orange-200 border-orange-300 text-orange-800',
              'from-emerald-100 to-emerald-200 border-emerald-300 text-emerald-800',
              'from-green-100 to-green-200 border-green-300 text-green-800'
            ];
            const colorClass = colors[index % colors.length];

            return (
              <div key={option.id} className={`bg-gradient-to-r ${colorClass} p-3 rounded-lg border-2 hover:shadow-md transition-all duration-200 cursor-pointer ${selectedRPMStatuses.includes(option.status) ? 'ring-2 ring-teal-400 shadow-lg' : ''}`}>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`rpm-${option.id}`}
                    checked={selectedRPMStatuses.includes(option.status)}
                    onCheckedChange={(checked) =>
                      onRPMStatusChange(option.status, checked as boolean)
                    }
                    className="data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
                    data-testid={`status-selection-rpm-checkbox-${option.status.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                  <label htmlFor={`rpm-${option.id}`} className="text-sm font-medium cursor-pointer flex-1">
                    {option.status}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Combined CCM and RPM Status Selection
  if (searchBy === 'ccm_and_rpm_status') {
    return (
      <div className="mt-4 p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 rounded-xl border border-purple-200 shadow-lg" data-testid="status-selection-combined">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-purple-700">Select status options below</h3>
          <button
            onClick={onResetAllStatuses}
            className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full flex items-center justify-center transition-all cursor-pointer"
            title="Reset All Status Selections"
            data-testid="status-selection-combined-reset-button"
          >
            <span className="text-white text-sm font-bold">↻</span>
          </button>
        </div>

        <div className="space-y-6">
          {/* CCM Status Section */}
          <div>
            <h4 className="text-lg font-semibold text-gray-700 mb-4">Select CCM Status</h4>
            <div className="grid grid-cols-4 gap-3">
              {ccmStatusOptions.map((option, index) => {
                const colors = [
                  'from-blue-100 to-blue-200 border-blue-300 text-blue-800',
                  'from-green-100 to-green-200 border-green-300 text-green-800',
                  'from-purple-100 to-purple-200 border-purple-300 text-purple-800',
                  'from-pink-100 to-pink-200 border-pink-300 text-pink-800',
                  'from-indigo-100 to-indigo-200 border-indigo-300 text-indigo-800',
                  'from-teal-100 to-teal-200 border-teal-300 text-teal-800',
                  'from-orange-100 to-orange-200 border-orange-300 text-orange-800',
                  'from-cyan-100 to-cyan-200 border-cyan-300 text-cyan-800',
                  'from-emerald-100 to-emerald-200 border-emerald-300 text-emerald-800',
                  'from-rose-100 to-rose-200 border-rose-300 text-rose-800'
                ];
                const colorClass = colors[index % colors.length];

                return (
                  <div key={option.id} className={`bg-gradient-to-r ${colorClass} p-3 rounded-lg border-2 hover:shadow-md transition-all duration-200 cursor-pointer ${selectedCCMStatuses.includes(option.status) ? 'ring-2 ring-purple-400 shadow-lg' : ''}`}>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`ccm-combo-${option.id}`}
                        checked={selectedCCMStatuses.includes(option.status)}
                        onCheckedChange={(checked) =>
                          onCCMStatusChange(option.status, checked as boolean)
                        }
                        className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                        data-testid={`status-selection-combined-ccm-checkbox-${option.status.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <label htmlFor={`ccm-combo-${option.id}`} className="text-sm font-medium cursor-pointer flex-1">
                        {option.status}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RPM Status Section */}
          <div>
            <h4 className="text-lg font-semibold text-gray-700 mb-4">Select RPM Status</h4>
            <div className="grid grid-cols-4 gap-3">
              {rpmStatusOptions.map((option, index) => {
                const colors = [
                  'from-teal-100 to-teal-200 border-teal-300 text-teal-800',
                  'from-cyan-100 to-cyan-200 border-cyan-300 text-cyan-800',
                  'from-blue-100 to-blue-200 border-blue-300 text-blue-800',
                  'from-indigo-100 to-indigo-200 border-indigo-300 text-indigo-800',
                  'from-purple-100 to-purple-200 border-purple-300 text-purple-800',
                  'from-pink-100 to-pink-200 border-pink-300 text-pink-800',
                  'from-rose-100 to-rose-200 border-rose-300 text-rose-800',
                  'from-orange-100 to-orange-200 border-orange-300 text-orange-800',
                  'from-emerald-100 to-emerald-200 border-emerald-300 text-emerald-800',
                  'from-green-100 to-green-200 border-green-300 text-green-800'
                ];
                const colorClass = colors[index % colors.length];

                return (
                  <div key={option.id} className={`bg-gradient-to-r ${colorClass} p-3 rounded-lg border-2 hover:shadow-md transition-all duration-200 cursor-pointer ${selectedRPMStatuses.includes(option.status) ? 'ring-2 ring-teal-400 shadow-lg' : ''}`}>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`rpm-combo-${option.id}`}
                        checked={selectedRPMStatuses.includes(option.status)}
                        onCheckedChange={(checked) =>
                          onRPMStatusChange(option.status, checked as boolean)
                        }
                        className="data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
                        data-testid={`status-selection-combined-rpm-checkbox-${option.status.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <label htmlFor={`rpm-combo-${option.id}`} className="text-sm font-medium cursor-pointer flex-1">
                        {option.status}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default StatusSelectionComponent;
